import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CalendarClock, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatCurrency } from '@/lib/format';
import productApi from '@/lib/productApi';
import { toast } from '@/lib/toast';
import type { RateImpact, RateView } from '@/types/product';

const money = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? '—' : formatCurrency(cents / 100);

const today = () => new Date().toISOString().slice(0, 10);

import GroupedRates from './GroupedRates';
import { RATE_GROUP_ORDER } from './rateGroups';
import RateRemovalDialog from './RateRemovalDialog';

interface RatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
  canDelete?: boolean;
}

/**
 * Tarieven met een ingangsdatum, plus de bevestigingsstap bij een wijziging.
 *
 * De catalogus kent geen rollen: iedereen mag alles. Eén tariefwijziging raakt
 * elk product met uren en elke lopende offerte, dus de enige zinnige rem is dat
 * je eerst ziet wat je aanricht en er een reden bij zet. Die reden komt in de
 * history en is achteraf de enige verantwoording.
 */
export function RatesDialog({ open, onOpenChange, onChanged, canDelete = true }: RatesDialogProps) {
  const [rates, setRates] = useState<RateView[]>([]);
  const [removingRate, setRemovingRate] = useState<RateView | null>(null);
  const [editing, setEditing] = useState<{
    rateKey: string;
    amount: string;
    validFrom: string;
    reason: string;
  } | null>(null);
  const [impact, setImpact] = useState<RateImpact | null>(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState<{
    key: string;
    name: string;
    amount: string;
    group: string;
  } | null>(null);

  const load = useCallback(async () => {
    try {
      setRates(await productApi.rates());
    } catch (error) {
      toast.error('Tarieven konden niet geladen worden', { error });
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  useEffect(() => {
    if (!editing) {
      setImpact(null);
      return;
    }
    const cents = Math.round(Number(editing.amount.replace(',', '.') || 0) * 100);
    if (!cents) return;

    setLoadingImpact(true);
    const timer = window.setTimeout(() => {
      productApi
        .previewRateChange({
          rateKey: editing.rateKey,
          amountCents: cents,
          validFrom: new Date(editing.validFrom).toISOString(),
        })
        .then(setImpact)
        .catch(() => setImpact(null))
        .finally(() => setLoadingImpact(false));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [editing]);

  const handleApply = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await productApi.applyRateChange({
        rateKey: editing.rateKey,
        amountCents: Math.round(Number(editing.amount.replace(',', '.') || 0) * 100),
        validFrom: new Date(editing.validFrom).toISOString(),
        reason: editing.reason,
      });
      setEditing(null);
      await load();
      onChanged();
      toast.success('Tariefwijziging vastgelegd');
    } catch (error) {
      toast.error('Wijziging is niet gelukt', { error });
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    if (!creating) return;
    try {
      await productApi.createRate({
        key: creating.key,
        name: creating.name,
        group: creating.group || undefined,
        amountCents: Math.round(Number(creating.amount.replace(',', '.') || 0) * 100),
      });
      setCreating(null);
      await load();
      onChanged();
    } catch (error) {
      toast.error('Tarief aanmaken is niet gelukt', { error });
    }
  };

  const affectedProducts = impact?.products.length ?? 0;
  const affectedQuotes = impact?.quotes.length ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tarieven</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Producten verwijzen naar een tarief, niet naar een bedrag. Een wijziging krijgt een
          ingangsdatum, zodat je een prijsronde vooruit kunt plannen zonder dat er vandaag iets
          verandert. Offerteregels houden het tarief van het moment waarop ze werden toegevoegd.
        </p>

        <GroupedRates
          rates={rates}
          renderRate={(rate) => (
            <div key={rate.id} className="py-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">{rate.name}</div>
                  <code className="text-xs text-muted-foreground">{rate.key}</code>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold">{money(rate.currentCents)}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setEditing({
                        rateKey: rate.key,
                        amount: ((rate.currentCents ?? 0) / 100).toString(),
                        validFrom: today(),
                        reason: '',
                      })
                    }
                  >
                    Wijzigen
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={!canDelete || busy}
                    aria-label={`Tarief ${rate.name} verwijderen`}
                    title={
                      canDelete
                        ? 'Tarief verwijderen en vervangen'
                        : 'Pas eerst je wijzigingen toe of verwerp ze'
                    }
                    onClick={() => setRemovingRate(rate)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {rate.upcoming && (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>
                    Gepland: {money(rate.upcoming.amountCents)} vanaf{' '}
                    {new Date(rate.upcoming.validFrom).toLocaleDateString('nl-NL')}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={async () => {
                      await productApi.deleteRateEntry(rate.upcoming!.id);
                      await load();
                      onChanged();
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          )}
        />

        {creating ? (
          <div className="space-y-3 rounded-md border p-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Sleutel</Label>
                <Input
                  value={creating.key}
                  placeholder="development"
                  onChange={(event) => setCreating({ ...creating, key: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Naam</Label>
                <Input
                  value={creating.name}
                  placeholder="Development"
                  onChange={(event) => setCreating({ ...creating, name: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Bedrag per uur</Label>
                <Input
                  value={creating.amount}
                  onChange={(event) => setCreating({ ...creating, amount: event.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-rate-group">Groep</Label>
              <Input
                id="new-rate-group"
                list="rate-groups"
                value={creating.group}
                placeholder="Bijv. Marketing"
                onChange={(event) => setCreating({ ...creating, group: event.target.value })}
              />
              <datalist id="rate-groups">
                {RATE_GROUP_ORDER.map((group) => (
                  <option key={group} value={group} />
                ))}
              </datalist>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreate}>
                Aanmaken
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setCreating(null)}>
                Annuleren
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => setCreating({ key: '', name: '', amount: '', group: '' })}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Tarief toevoegen
          </Button>
        )}

        {editing && (
          <div className="space-y-4 rounded-md border-2 border-primary/40 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Nieuw bedrag per uur</Label>
                <Input
                  value={editing.amount}
                  onChange={(event) => setEditing({ ...editing, amount: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Geldig vanaf</Label>
                <Input
                  type="date"
                  value={editing.validFrom}
                  onChange={(event) => setEditing({ ...editing, validFrom: event.target.value })}
                />
              </div>
            </div>

            {loadingImpact && <p className="text-sm text-muted-foreground">Impact berekenen…</p>}

            {impact && (
              <Alert variant={affectedQuotes > 0 ? 'destructive' : 'default'}>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="space-y-2">
                  <div>
                    Deze wijziging raakt <strong>{affectedProducts} producten</strong> en{' '}
                    <strong>
                      {affectedQuotes} lopende offerte{affectedQuotes === 1 ? '' : 's'}
                    </strong>
                    . Lopende offertes veranderen niet vanzelf: ze houden het tarief van het moment
                    waarop de regel is toegevoegd en krijgen de melding &quot;product
                    gewijzigd&quot;.
                  </div>

                  {impact.products.length > 0 && (
                    <div className="max-h-40 overflow-y-auto text-sm">
                      {impact.products.slice(0, 12).map((product) => (
                        <div key={product.id} className="flex justify-between gap-3 py-0.5">
                          <span className="truncate">{product.name}</span>
                          <span className="whitespace-nowrap text-muted-foreground">
                            {product.variants
                              .slice(0, 2)
                              .map(
                                (variant) =>
                                  `${money(variant.currentCents)} → ${money(variant.nextCents)}`
                              )
                              .join(', ')}
                          </span>
                        </div>
                      ))}
                      {impact.products.length > 12 && (
                        <div className="py-0.5 text-muted-foreground">
                          en nog {impact.products.length - 12} producten…
                        </div>
                      )}
                    </div>
                  )}

                  {impact.quotes.length > 0 && (
                    <div className="text-sm">
                      <span className="font-medium">Lopende offertes: </span>
                      {impact.quotes
                        .slice(0, 5)
                        .map((quote) => quote.companyName || quote.title)
                        .join(', ')}
                      {impact.quotes.length > 5 && ` en nog ${impact.quotes.length - 5} andere`}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="rate-reason">Reden (verplicht)</Label>
              <Textarea
                id="rate-reason"
                rows={2}
                value={editing.reason}
                onChange={(event) => setEditing({ ...editing, reason: event.target.value })}
                placeholder="Bijvoorbeeld: prijsronde 2026, ingangsdatum 1 januari."
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleApply} disabled={busy || !editing.reason.trim()}>
                {busy ? 'Bezig…' : 'Doorvoeren'}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>
                Annuleren
              </Button>
              {!editing.reason.trim() && (
                <span className="self-center text-xs text-muted-foreground">
                  Vul eerst een reden in.
                </span>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Sluiten
          </Button>
        </DialogFooter>
        <RateRemovalDialog
          rate={removingRate}
          rates={rates}
          onClose={() => setRemovingRate(null)}
          onRemoved={async () => {
            await load();
            onChanged();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export default RatesDialog;
